import { Component, Input, OnInit, ViewChildren } from '@angular/core';
import { TireFormData, TiresFormService } from '../../services/tires-form.service';
import { Observable } from 'rxjs';
import { map, takeUntil, take } from 'rxjs/operators';
import {
  ConfigurationState,
  PostingDataResponseModel,
  EditPostingState,
  SetTireFormsCertifiedOrValid,
  SetTiresCertifiedByUser,
  UpdatePosting,
  EditPostingFormNames,
  NewPostingInspectionOption,
  PostingTypes
} from 'remarketing-angular-library';
import { TableDisplayStyle, TableModule } from 'remarketing-ui-components-lib';
import { Store } from '@ngxs/store';
import { cloneDeep, get } from 'lodash';
import { PostingFormDirective } from '../posting-form/posting-form.directive';
import { PostingFormTrackingService } from '../../services/posting-form-tracking.service';
import { TireDescriptionFormComponent } from '../tire-description-form/tire-description-form.component';
import { PostingAnalyticsHelperService } from '../../services/posting-analytics-helper.service';
import { TireValidationHelperService } from '../../services/tire-validation-helper.service';
import { CurrencyFormatService, fromMatchMedia } from 'smartauction-frontend-micro-library';
import { EditPostingUtilsService } from '../../services/edit-posting-utils.service';
import { TranslocoService } from '@jsverse/transloco';
import { CommonModule } from '@angular/common';

const locationNames = {
  FL: 'Left Front',
  FR: 'Right Front',
  RL: 'Left Rear',
  RR: 'Right Rear',
  SP: 'Spare'
};

@Component({
    selector: 'app-tires-form',
    templateUrl: './tires-form.component.html',
    styleUrls: ['./tires-form.component.scss'],
    imports: [TableModule, TireDescriptionFormComponent, CommonModule]
})
export class TiresFormComponent extends PostingFormDirective implements OnInit {
  @ViewChildren(TireDescriptionFormComponent) tireForms;
  @Input() postingTypeString: string;
  postingData: PostingDataResponseModel;
  vehicleVINNumber: string;
  tires = [];
  tireTableStyle$: Observable<TableDisplayStyle> = fromMatchMedia('(min-width: 768px)').pipe(
    map(({ matches }) => (matches ? 'table' : 'cards'))
  );

  tireCheckboxCertified = false;
  formsActiveInUserSessionMap;
  postingType;
  checked: string;
  tiresFormTranslationObj: any;

  get isSelectionValid(): boolean {
    return (
      this.tireCheckboxCertified || this.tireValidationHelperService.isAllFourRequiredTiresDataPresent(this.postingData)
    );
  }

  get formsExist(): boolean {
    return !!this.tireForms && this.tireForms.length > 0;
  }

  get isNewPosting(): boolean {
    return this.postingType === PostingTypes.NEW;
  }

  get isExistingPosting(): boolean {
    return this.postingType === PostingTypes.EDIT || this.postingType === PostingTypes.REPOST_BEFORE_60_DAYS;
  }

  get isUsingExistingInspection(): boolean {
    return (
      this.store.selectSnapshot(EditPostingState.selectedInspectionOption) === NewPostingInspectionOption.USE_EXISTING
    );
  }

  constructor(
    private readonly tiresFormService: TiresFormService,
    private readonly store: Store,
    private readonly editPostingUtilsService: EditPostingUtilsService,
    private readonly postingFormTrackingService: PostingFormTrackingService,
    private readonly tireValidationHelperService: TireValidationHelperService,
    private readonly postingAnalyticsHelperService: PostingAnalyticsHelperService,
    private readonly currencyFormatService: CurrencyFormatService,
    private readonly translocoService: TranslocoService
  ) {
    super();
  }

  ngOnInit() {
    this.getTranslations();
    this.store
      .select(EditPostingState.updatedPosting)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(postingData => {
        this.postingData = cloneDeep(postingData);
      });
    this.vehicleVINNumber = this.postingData.inventoryItem.vehicleInformation.vin;
    this.postingType = this.store.selectSnapshot(EditPostingState.postingType);

    this.tires = get(this.postingData, 'inventoryItem.inspectionReport.inspectionData.vehicle.tires', []);
    if (this.postingType === PostingTypes.NEW) {
      this.handleTiresCertifiedCheckboxInitialValueOnNewPost();
    } else {
      this.handleTireCertifiedCheckboxInitialValueOnExistingPosting();
    }

    //Called for form tracking on reload
    this.postingFormTrackingService.syncFormToInvalidValidationFormsOnLoad(
      this.postingType,
      EditPostingFormNames.DISCLOSURES_TIRES
    );

    this.handleInitialFormTrackingValidation();
  }

  formatCurrency(value: string): string {
    const valueToNumber = parseFloat(value);
    return this.currencyFormatService.formatCurrency(valueToNumber, true);
  }

  handleInitialFormTrackingValidation(): void {
    if ((this.isNewPosting && this.isUsingExistingInspection) || this.isExistingPosting) {
      this.editPostingUtilsService.addToValidationForms(this.isSelectionValid, EditPostingFormNames.DISCLOSURES_TIRES);
    }
    this.store.dispatch(new SetTireFormsCertifiedOrValid(this.isSelectionValid));
    this.sendPageloadAnalyticsEvent();
  }

  sendPageloadAnalyticsEvent() {
    this.postingAnalyticsHelperService.sendPageloadAnalyticsEvent(
      this.store.selectSnapshot(ConfigurationState.configuration).tenantName,
      this.postingTypeString,
      EditPostingFormNames.DISCLOSURES_TIRES
    );
  }

  handleTiresCertifiedCheckboxInitialValueOnNewPost(): void {
    // Below logic needed on load and page refresh when creating new post
    this.postingData.inventoryItem.inspectionReport.inspectionData.vehicle.tires = this.tires;
    if (this.store.selectSnapshot(EditPostingState.userHasCertifiedTires)) {
      this.store.dispatch(new UpdatePosting(this.postingData));
      this.tireCheckboxCertified = true;
    } else {
      this.store.dispatch(new UpdatePosting(this.postingData));
      this.tireCheckboxCertified = false;
    }
  }

  handleTireCertifiedCheckboxInitialValueOnExistingPosting(): void {
    const userHasCertifiedTires = this.store.selectSnapshot(EditPostingState.userHasCertifiedTires);

    if (this.tires === undefined || this.tires === null) {
      this.setTireCertificationCheckboxAndState(true);
    } else if (this.tires.length === 0 && !userHasCertifiedTires) {
      // Initial Load Scenario when no tires present
      this.setTireCertificationCheckboxAndState(true);
    } else if (this.tires.length === 0 && userHasCertifiedTires) {
      this.tireCheckboxCertified = true;
    } else {
      this.setTireCertificationCheckboxAndState(false);
    }
  }

  setTireCertificationCheckboxAndState(value: boolean): void {
    this.store.dispatch(new SetTiresCertifiedByUser(value));
    this.tireCheckboxCertified = value;
  }

  analyticsLocationName(location: string) {
    return locationNames[location] || '';
  }

  trackByLocation(index: number, tire: TireFormData) {
    return tire.location;
  }

  toggleDrawer(location: string) {
    this.tiresFormService.toggleDrawer(location);
  }

  onUpdateForm({ markTheSame, ...data }: TireFormData & { markTheSame: boolean }) {
    if (markTheSame) {
      this.tiresFormService.updateAllTires(data);
    } else {
      this.tiresFormService.updateTire(data);
    }
    let locationToExpand = '';
    if (data.location === 'FL') {
      locationToExpand = 'FR';
    } else if (data.location === 'FR') {
      locationToExpand = 'RL';
    } else if (data.location === 'RL') {
      locationToExpand = 'RR';
    }
    this.toggleDrawer(data.location);
    if (!markTheSame) {
      this.toggleDrawer(locationToExpand);
    }

    this.editPostingUtilsService.addToValidationForms(this.isSelectionValid, EditPostingFormNames.DISCLOSURES_TIRES);
    this.store.dispatch(new SetTireFormsCertifiedOrValid(this.isSelectionValid));
  }

  maybeCertify(e) {
    // FUTURE: Conditionally mark certified based on the DIRTY state of the child forms
    this.tireCheckboxCertified = e.target.checked;
    if (this.tireCheckboxCertified) {
      this.postingData.inventoryItem.inspectionReport.inspectionData.vehicle.tires = [];
      this.store.dispatch(new UpdatePosting(this.postingData));
      this.store.dispatch(new SetTiresCertifiedByUser(true));
    } else {
      this.store.dispatch(new SetTiresCertifiedByUser(false));
    }
    this.handleFormTrackingAndStateForTireCertificationCheckboxEvent();
  }

  handleFormTrackingAndStateForTireCertificationCheckboxEvent(): void {
    if (this.formsExist) {
      this.editPostingUtilsService.addToValidationForms(this.isSelectionValid, EditPostingFormNames.DISCLOSURES_TIRES);
      this.store.dispatch(new SetTireFormsCertifiedOrValid(this.isSelectionValid));
    } else {
      // This is the case where a user has not yet expanded the forms but is checking/unchecking the tire certification box
      this.editPostingUtilsService.addToValidationForms(
        this.tireCheckboxCertified,
        EditPostingFormNames.DISCLOSURES_TIRES
      );
      this.store.dispatch(new SetTireFormsCertifiedOrValid(this.isSelectionValid));
    }
  }

  getTranslations(): void {
    // > 20 translations for 'tireForm,' done as object for ease of reference in DOM
    this.translocoService
      .selectTranslateObject('sellModule.posting')
      .pipe(takeUntil(this.destroyed$))
      .subscribe(translatedObj => {
        this.tiresFormTranslationObj = translatedObj;
      });

    // singular 'common' translation
    this.translocoService
      .selectTranslate('common.checked')
      .pipe(takeUntil(this.destroyed$))
      .subscribe(translation => {
        this.checked = translation;
      });
  }
}
